'use client';

import { useState } from 'react';

import Link from 'next/link';

import {
  BadgeDollarSign,
  BarChart3,
  CheckCircle2,
  CircleMinus,
  Download,
  ExternalLink,
  Megaphone,
  Network,
  PackageCheck,
  Percent,
  ReceiptText,
  RotateCcw,
  Ship,
  TriangleAlert,
  Warehouse,
} from 'lucide-react';

import {
  DEFAULT_CURRENCY,
  formatMoney,
  getCurrencyName,
  getCurrencySymbol,
} from '@kit/shared/currency';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';

import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';

import { CurrencyPicker } from '../_components/currency-picker';
import { RelatedTools } from '../_components/related-tools';
import {
  type AmazonProfitInputs,
  calculateAmazonProfit,
  normalizeAmazonProfitInput,
} from './_lib/calculate-amazon-profit';

const baseUrl = appConfig.url;
const calculatorUrl = `${baseUrl}/tools/amazon-profit-calculator`;
const toolsUrl = `${baseUrl}/tools`;
const amazonRevenueCalculatorUrl =
  'https://sellercentral.amazon.com/hz/fba/profitabilitycalculator/index?lang=en_US';
type FormData = Record<keyof AmazonProfitInputs, string>;

const DEFAULT_FORM_DATA: FormData = {
  advertisingPercent: '10',
  fulfillmentFee: '7',
  landedCost: '10',
  monthlyUnits: '300',
  otherCost: '0',
  referralFeePercent: '15',
  returnsPercent: '2',
  sellingPrice: '25',
  storageFee: '0.75',
};
const faqItems = [
  {
    value: 'realistic-profit',
    question:
      'What is a realistic Amazon profit on a $25 sale with a $10 landed cost?',
    answer:
      'The page opens with that private-label example: 15% referral ($3.75), $7 FBA, $0.75 storage, 2% returns ($0.50). That leaves $3.00 before ads and $0.50 after a 10% advertising allowance (2% margin, 5% ROI on landed cost). Break-even ACoS is 12%. At 300 units a month that is $150 profit on $7,500 revenue. Replace the fees with Amazon’s current category rates before you treat $0.50 as yours.',
  },
  {
    value: 'landed-vs-fees',
    question: 'Should Amazon fees go into landed cost?',
    answer:
      'No. Landed cost is inbound: product, freight, duty, prep, and delivery into Amazon. Referral, FBA fulfillment, storage, and ads are the selling stack. This calculator keeps them as separate inputs so you can see which side is the problem. The two-column method is in how to calculate Amazon FBA landed cost.',
  },
  {
    value: 'free',
    question: 'Is this FBA calculator free?',
    answer:
      'Yes. This free FBA calculator runs entirely in your browser with no signup, no usage limit, and no credit card. You can export any scenario to CSV.',
  },
  {
    value: 'referral-fee',
    question: 'Is the Amazon referral fee always 15%?',
    answer:
      'No. Referral fee percentages vary by product category and can include minimum fees. Enter the rate that applies to the exact category instead of treating 15% as universal.',
  },
  {
    value: 'weight',
    question: 'Is product weight enough to estimate the FBA fulfillment fee?',
    answer:
      'No. Amazon uses size tiers and shipping weight rules, so package dimensions and dimensional weight can materially change the fulfillment fee. Use the exact packaged dimensions and weight in Amazon’s Revenue Calculator, then enter that fee here.',
  },
  {
    value: 'acos',
    question: 'What is break-even ACoS?',
    answer:
      'Break-even ACoS is the maximum advertising cost as a percentage of selling price that leaves zero per-unit profit after the other costs in this model. Spending below it leaves contribution profit; spending above it creates a loss.',
  },
  {
    value: 'returns',
    question: 'Why include a returns reserve?',
    answer:
      'Returns can create refund deductions, damaged inventory, removal costs, and lost fulfillment fees. A reserve turns that uneven expense into a realistic per-sale allowance for planning.',
  },
  {
    value: 'official-calculator',
    question: 'Does this replace Amazon’s Revenue Calculator?',
    answer:
      'No. Use Amazon’s official calculator to get the current product-specific referral and fulfillment fees. This tool adds the broader private-label economics that sellers often miss, including landed cost, advertising, returns, overhead, margin, ROI, and monthly profit.',
  },
] as const;

const economicsCards = [
  {
    title: 'Landed cost',
    description:
      'Use the cost of one sellable unit delivered into Amazon, including product, freight, duty, prep, and inbound costs.',
    icon: PackageCheck,
  },
  {
    title: 'Amazon fees',
    description:
      'Enter the exact referral and FBA fulfillment fees for the category, packaged dimensions, weight, and program.',
    icon: Warehouse,
  },
  {
    title: 'Advertising',
    description:
      'Model paid acquisition as a percentage of revenue and compare it with your break-even ACoS.',
    icon: Megaphone,
  },
  {
    title: 'Returns and overhead',
    description:
      'Reserve for refunds, damaged inventory, software, inspection, coupons, and other costs that do not appear on the supplier invoice.',
    icon: ReceiptText,
  },
] as const;

function parseInput(value: string) {
  const parsed = Number.parseFloat(value.replaceAll(',', ''));
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

function formatPercent(value: number | null) {
  return value === null ? 'N/A' : `${value.toFixed(1)}%`;
}

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Amazon Profit Calculator: After Fees, Ads, Landed Cost',
    alternateName: 'FBA Profit Calculator',
    description:
      'Calculate Amazon FBA profit after landed cost, referral, fulfillment, ads, and returns. See margin, ROI, and break-even ACoS.',
    url: calculatorUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    creator: { '@type': 'Organization', name: appConfig.name, url: baseUrl },
    featureList: [
      'Calculate Amazon profit per unit',
      'Estimate monthly Amazon profit',
      'Calculate profit margin and ROI',
      'Calculate break-even ACoS',
      'Model referral, fulfillment, storage, advertising, and return costs',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Free Tools', item: toolsUrl },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Amazon Profit Calculator',
        item: calculatorUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

function CalculatorInput({
  help,
  id,
  label,
  onChange,
  prefix,
  prefixCurrency,
  step = '0.01',
  suffix,
  value,
}: {
  help?: string;
  id: string;
  label: string;
  onChange: (value: string) => void;
  prefix?: string;
  /** ISO code announced to screen readers, since several currencies share "$". */
  prefixCurrency?: string;
  step?: string;
  suffix?: string;
  value: string;
}) {
  const helpId = help ? `${id}-help` : undefined;
  const unitId = prefix || suffix ? `${id}-unit` : undefined;
  const describedBy = [unitId, helpId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-foreground text-sm font-bold">
        {label}
      </Label>
      <div className="relative">
        {prefix ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-sm"
          >
            {prefix}
          </span>
        ) : null}
        <Input
          id={id}
          aria-describedby={describedBy}
          type="number"
          min="0"
          max={suffix === '%' ? '100' : undefined}
          step={step}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`border-border bg-muted h-12 rounded-xl shadow-none ${
            prefix ? 'pl-8' : ''
          } ${suffix ? 'pr-9' : ''}`}
        />
        {suffix ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-sm"
          >
            {suffix}
          </span>
        ) : null}
      </div>
      {unitId ? (
        <span id={unitId} className="sr-only">
          {prefixCurrency
            ? `Amount in ${getCurrencyName(prefixCurrency)}.`
            : null}
          {suffix === '%' ? 'Percentage from 0 to 100.' : null}
        </span>
      ) : null}
      {help ? (
        <p id={helpId} className="text-muted-foreground text-xs leading-5">
          {help}
        </p>
      ) : null}
    </div>
  );
}

export default function AmazonProfitCalculatorPage() {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  const formatCurrency = (value: number) => formatMoney({ currency, value });
  const currencySymbol = getCurrencySymbol(currency);
  const startFreePath = pathsConfig.auth.startFree;
  const inputs: AmazonProfitInputs = {
    advertisingPercent: parseInput(formData.advertisingPercent),
    fulfillmentFee: parseInput(formData.fulfillmentFee),
    landedCost: parseInput(formData.landedCost),
    monthlyUnits: parseInput(formData.monthlyUnits),
    otherCost: parseInput(formData.otherCost),
    referralFeePercent: parseInput(formData.referralFeePercent),
    returnsPercent: parseInput(formData.returnsPercent),
    sellingPrice: parseInput(formData.sellingPrice),
    storageFee: parseInput(formData.storageFee),
  };
  const result = calculateAmazonProfit(inputs);
  const isProfitable = result.profitPerUnit > 0;
  const isLoss = result.profitPerUnit < 0;

  function updateField(key: keyof FormData, value: string) {
    setFormData((current) => ({
      ...current,
      [key]: normalizeAmazonProfitInput(key, value),
    }));
  }

  function resetExample() {
    setFormData(DEFAULT_FORM_DATA);
  }

  function downloadReport() {
    const rows = [
      ['Metric', 'Value'],
      ['Selling price', inputs.sellingPrice.toFixed(2)],
      ['Landed cost per unit', inputs.landedCost.toFixed(2)],
      ['Referral fee percent', inputs.referralFeePercent.toFixed(2)],
      ['FBA fulfillment fee', inputs.fulfillmentFee.toFixed(2)],
      ['Storage fee per unit', inputs.storageFee.toFixed(2)],
      ['Advertising percent', inputs.advertisingPercent.toFixed(2)],
      ['Returns reserve percent', inputs.returnsPercent.toFixed(2)],
      ['Other costs per unit', inputs.otherCost.toFixed(2)],
      ['Monthly units', inputs.monthlyUnits.toFixed(0)],
      ['Profit per unit', result.profitPerUnit.toFixed(2)],
      ['Profit margin percent', result.profitMarginPercent.toFixed(1)],
      [
        'ROI percent',
        result.roiPercent === null ? 'N/A' : result.roiPercent.toFixed(1),
      ],
      ['Break-even ACoS percent', result.breakEvenAcosPercent.toFixed(1)],
      ['Monthly revenue', result.monthlyRevenue.toFixed(2)],
      ['Monthly profit', result.monthlyProfit.toFixed(2)],
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `amazon-profit-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const costRows = [
    { label: 'Landed cost', value: inputs.landedCost },
    { label: 'Referral fee', value: result.referralFee },
    { label: 'FBA fulfillment', value: inputs.fulfillmentFee },
    { label: 'Storage', value: inputs.storageFee },
    { label: 'Advertising', value: result.advertisingCost },
    { label: 'Returns reserve', value: result.returnsReserve },
    { label: 'Other costs', value: inputs.otherCost },
  ];

  return (
    <>
      <StructuredData />

      <div className="bg-background text-foreground min-h-screen">
        <section className="border-border relative overflow-hidden border-b">
          <div className="absolute inset-x-0 top-0 h-[480px] bg-[radial-gradient(circle_at_top_right,_color-mix(in_srgb,var(--primary)_calc(0.13*100%),transparent),_transparent_44%)]" />
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="max-w-2xl">
                <div className="bg-primary/10 text-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase">
                  <BadgeDollarSign className="h-4 w-4" />
                  Free Amazon seller tool
                </div>

                <h1 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                  <span className="text-primary">Amazon Profit Calculator</span>
                  : after fees, ads, and landed cost
                </h1>
                <p className="text-muted-foreground mt-6 max-w-xl text-lg leading-8">
                  A $25 listing with a $10 landed cost looks like $15. After a
                  15% referral, $7 FBA, $0.75 storage, 2% returns, and 10% ads,
                  this page leaves <strong>$0.50</strong> per unit and a 12%
                  break-even ACoS. Replace every field with your SKU.
                </p>

                <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
                  {[
                    'Uses the fees you enter, not a guessed Amazon rate',
                    'Keeps inbound landed cost separate from the fee stack',
                    'No signup required',
                  ].map((item) => (
                    <span
                      key={item}
                      className="text-muted-foreground inline-flex items-center gap-2 text-sm font-medium"
                    >
                      <CheckCircle2 className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ))}
                </div>

                <p className="text-muted-foreground mt-8 text-sm leading-6">
                  The calculator opens with a typical private-label example.
                  Replace every field with your own product numbers to model
                  your real unit economics.
                </p>
              </div>

              <div
                aria-live="polite"
                className="bg-primary text-on-primary rounded-[10px] p-7 shadow-[0_24px_70px_-30px_rgba(38,31,28,0.7)] sm:p-9"
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="text-on-primary text-xs font-bold tracking-[0.18em] uppercase">
                      Estimated profit per unit
                    </p>
                    {/* No colour shift for the loss state: every red dark
                        enough to read on this coral is illegible, and no
                        lighter one clears 3:1. The minus sign and the icon
                        swap below carry the signal instead. */}
                    <p className="mt-3 text-5xl font-extrabold tracking-tight tabular-nums">
                      {formatCurrency(result.profitPerUnit)}
                    </p>
                  </div>
                  <div className="bg-on-primary/10 rounded-2xl p-3">
                    {isProfitable ? (
                      <BarChart3 className="h-7 w-7" />
                    ) : isLoss ? (
                      <TriangleAlert className="h-7 w-7" />
                    ) : (
                      <CircleMinus className="h-7 w-7" />
                    )}
                  </div>
                </div>

                <div className="border-on-primary/20 mt-8 grid grid-cols-2 gap-4 border-t pt-7">
                  {[
                    [
                      'Profit margin',
                      formatPercent(result.profitMarginPercent),
                    ],
                    ['ROI on landed cost', formatPercent(result.roiPercent)],
                    [
                      'Break-even ACoS',
                      formatPercent(result.breakEvenAcosPercent),
                    ],
                    ['Monthly profit', formatCurrency(result.monthlyProfit)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-on-primary/10 rounded-xl p-4"
                    >
                      <p className="text-on-primary text-xs">{label}</p>
                      <p className="mt-1 text-lg font-bold tabular-nums">
                        {value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between gap-4 text-sm">
                  <span className="text-on-primary">Profit before ads</span>
                  <span className="font-bold tabular-nums">
                    {formatCurrency(result.preAdvertisingProfit)}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-border bg-card mt-12 rounded-[10px] border p-6 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)] sm:p-8 lg:p-10">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-2xl font-extrabold">
                    Calculate Amazon FBA profit
                  </h2>
                  <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-6">
                    The defaults are an illustrative example with realistic
                    planning allowances. Replace them with your exact product
                    numbers before making a sourcing decision.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetExample}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset example
                  </Button>
                  <Button type="button" onClick={downloadReport}>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>
              </div>

              <form
                onSubmit={(event) => event.preventDefault()}
                className="mt-8 grid gap-x-6 gap-y-6 md:grid-cols-2 lg:grid-cols-3"
              >
                <CurrencyPicker
                  hint="Enter every figure in this currency."
                  onChange={setCurrency}
                  value={currency}
                />

                <CalculatorInput
                  id="sellingPrice"
                  label="Selling price"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  value={formData.sellingPrice}
                  onChange={(value) => updateField('sellingPrice', value)}
                />
                <CalculatorInput
                  id="landedCost"
                  label="Landed cost per unit"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="Sellable unit delivered into Amazon"
                  value={formData.landedCost}
                  onChange={(value) => updateField('landedCost', value)}
                />
                <CalculatorInput
                  id="referralFeePercent"
                  label="Referral fee"
                  suffix="%"
                  help="Varies by Amazon category"
                  value={formData.referralFeePercent}
                  onChange={(value) => updateField('referralFeePercent', value)}
                />
                <CalculatorInput
                  id="fulfillmentFee"
                  label="FBA fulfillment fee"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="Use Amazon's result for exact size and weight"
                  value={formData.fulfillmentFee}
                  onChange={(value) => updateField('fulfillmentFee', value)}
                />
                <CalculatorInput
                  id="storageFee"
                  label="Storage per unit sold"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="Average monthly storage allocated per sale"
                  value={formData.storageFee}
                  onChange={(value) => updateField('storageFee', value)}
                />
                <CalculatorInput
                  id="advertisingPercent"
                  label="Advertising cost"
                  suffix="%"
                  help="Ad spend as a percentage of sales"
                  value={formData.advertisingPercent}
                  onChange={(value) => updateField('advertisingPercent', value)}
                />
                <CalculatorInput
                  id="returnsPercent"
                  label="Returns reserve"
                  suffix="%"
                  help="Allowance for refunds and damaged inventory"
                  value={formData.returnsPercent}
                  onChange={(value) => updateField('returnsPercent', value)}
                />
                <CalculatorInput
                  id="otherCost"
                  label="Other cost per unit"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="Coupons, software, inspection, overhead"
                  value={formData.otherCost}
                  onChange={(value) => updateField('otherCost', value)}
                />
                <CalculatorInput
                  id="monthlyUnits"
                  label="Units sold per month"
                  step="1"
                  value={formData.monthlyUnits}
                  onChange={(value) => updateField('monthlyUnits', value)}
                />
              </form>

              <div className="border-border mt-8 flex flex-col gap-5 border-t pt-8 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 text-primary hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:flex">
                    <Network className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-base font-bold">
                      This estimate is only as good as your landed cost.
                    </p>
                    <p className="text-muted-foreground mt-1 max-w-xl text-sm leading-6">
                      {appConfig.name} tracks the real cost of every purchase
                      order, including supplier price, freight, duty, and prep,
                      so the number you model here matches what you actually
                      pay.
                    </p>
                  </div>
                </div>
                <Button
                  asChild
                  className="h-11 shrink-0 rounded-xl px-6 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card border-border border-b px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div>
                <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
                  Worked example
                </p>
                <h2 className="font-heading mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
                  What does a {formatCurrency(inputs.sellingPrice)} Amazon sale
                  actually leave?
                </h2>
                <p className="text-muted-foreground mt-5 text-base leading-8">
                  With the current inputs, selling price minus landed cost
                  leaves a{' '}
                  {formatCurrency(inputs.sellingPrice - inputs.landedCost)}{' '}
                  preliminary spread. After Amazon fees, advertising, storage,
                  and returns, the estimated profit is{' '}
                  <strong>{formatCurrency(result.profitPerUnit)}</strong> per
                  unit.
                </p>
                <div className="border-warn-fg/25 bg-warn-bg text-warn-fg mt-6 rounded-2xl border p-5 text-sm leading-6">
                  <div className="flex gap-3">
                    <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />
                    <p>
                      Shipping weight alone is not enough to determine the FBA
                      fee. Packaged dimensions and Amazon&apos;s size tier can
                      change the result materially.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-border bg-muted rounded-3xl border p-6 sm:p-8">
                <div className="flex items-center justify-between border-b pb-4">
                  <span className="font-bold">Selling price</span>
                  <span className="text-xl font-extrabold tabular-nums">
                    {formatCurrency(inputs.sellingPrice)}
                  </span>
                </div>
                <div className="mt-3 space-y-1">
                  {costRows.map((row) => (
                    <div
                      key={row.label}
                      className="text-muted-foreground flex items-center justify-between py-2 text-sm"
                    >
                      <span>{row.label}</span>
                      <span className="tabular-nums">
                        {row.value > 0 ? '-' : ''}
                        {formatCurrency(row.value)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-border mt-4 flex items-end justify-between border-t pt-5">
                  <div>
                    <p className="text-sm font-bold">Estimated profit</p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {formatPercent(result.profitMarginPercent)} margin
                    </p>
                  </div>
                  <span
                    className={`text-3xl font-extrabold tabular-nums ${
                      isProfitable
                        ? 'text-primary'
                        : isLoss
                          ? 'text-destructive'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {formatCurrency(result.profitPerUnit)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
                Amazon unit economics
              </p>
              <h2 className="font-heading mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
                Costs an Amazon profit calculator should include
              </h2>
              <p className="text-muted-foreground mt-5 text-base leading-8">
                Revenue minus product cost is not profit. A useful private-label
                model includes the costs that move with each sale and the
                reserves that appear unevenly over time.
              </p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {economicsCards.map((item) => {
                const Icon = item.icon;
                return (
                  <article
                    key={item.title}
                    className="border-border bg-card rounded-2xl border p-6 shadow-sm"
                  >
                    <div className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 text-lg font-bold">{item.title}</h3>
                    <p className="text-muted-foreground mt-2 text-sm leading-7">
                      {item.description}
                    </p>
                  </article>
                );
              })}
            </div>

            <div className="border-border bg-card mt-12 rounded-3xl border p-7 sm:p-10">
              <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
                <div>
                  <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-xl">
                    <Percent className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-2xl font-extrabold">
                    The profit formula
                  </h3>
                  <p className="text-muted-foreground mt-3 text-sm leading-7">
                    Break-even ACoS is calculated from the profit left before
                    advertising, divided by selling price.
                  </p>
                </div>
                <div className="bg-muted border-border rounded-2xl border p-6 text-center">
                  <p className="font-mono text-base leading-8 font-bold sm:text-lg">
                    Selling price - landed cost - Amazon fees - ads - returns -
                    other costs = profit
                  </p>
                  <p className="text-primary mt-4 text-sm font-bold">
                    Current break-even ACoS:{' '}
                    {formatPercent(result.breakEvenAcosPercent)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card border-border border-y px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="grid gap-10 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <h2 className="font-heading text-3xl font-extrabold tracking-tight">
                  Get the exact Amazon fee first
                </h2>
                <p className="text-muted-foreground mt-4 text-base leading-8">
                  Amazon fees vary by category, price, packaged dimensions,
                  shipping weight, size tier, storage period, and program.
                  Confirm the current referral and fulfillment fees in
                  Amazon&apos;s official calculator, then use this page for the
                  wider profit model.
                </p>
              </div>
              <Button asChild size="lg" className="h-12 rounded-xl px-6">
                <a
                  href={amazonRevenueCalculatorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Amazon Revenue Calculator
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section id="faq" className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-heading text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              Amazon profit calculator FAQ
            </h2>
            <div className="mt-10">
              <Accordion
                type="single"
                collapsible
                defaultValue="realistic-profit"
              >
                {faqItems.map((item) => (
                  <AccordionItem
                    key={item.value}
                    value={item.value}
                    className="border-border bg-card mb-4 overflow-hidden rounded-2xl border px-6"
                  >
                    <AccordionTrigger className="py-6 text-left text-base font-bold hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section className="bg-foreground text-background relative overflow-hidden py-24 sm:py-32">
          <div className="bg-primary/20 absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div className="max-w-xl">
              <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
                The calculator is free. The margin is the hard part.
              </p>
              <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                A spreadsheet estimate is not a landed cost.
              </h2>
              <p className="text-background/70 mt-6 text-lg leading-8">
                Every number above depends on what your unit truly costs
                delivered into Amazon. {appConfig.name} keeps purchase orders,
                suppliers, documents, freight, duty, and prep in one workspace,
                so your real cost per unit updates as the order moves, not three
                months later.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="bg-background text-foreground hover:bg-background/90 h-12 rounded-xl px-7 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="text-background hover:bg-background/10 h-12 rounded-xl border-white/25 bg-transparent px-7 font-bold"
                >
                  <Link href="/#features">See how it works</Link>
                </Button>
              </div>

              <div className="text-background/60 mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                  (item) => (
                    <span key={item} className="flex items-center gap-2">
                      <CheckCircle2 className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ),
                )}
              </div>
            </div>

            <figure className="bg-background text-foreground rounded-[32px] p-3 shadow-[0_32px_90px_-32px_rgba(0,0,0,0.7)] sm:p-4">
              <figcaption className="text-muted-foreground px-3 pb-3 text-xs font-bold tracking-[0.16em] uppercase">
                Illustrative {appConfig.name} workspace
              </figcaption>
              <div className="border-border bg-card overflow-hidden rounded-[8px] border">
                <div className="border-border flex items-center justify-between border-b px-5 py-4 sm:px-7">
                  <div className="flex items-center gap-3">
                    <div className="bg-foreground text-background flex h-9 w-9 items-center justify-center rounded-xl">
                      <Network className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold">{appConfig.name}</p>
                      <p className="text-muted-foreground text-xs">
                        Example FBA restock
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    On track
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.18em] uppercase">
                        Purchase order SA-1048
                      </p>
                      <p className="font-heading mt-2 text-3xl font-black sm:text-4xl">
                        $10.42
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Actual landed cost per unit
                      </p>
                    </div>
                    <div className="rounded-full bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      2,000 units
                    </div>
                  </div>

                  <div className="mt-8 h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                    <div className="bg-primary h-full w-[82%] rounded-full" />
                  </div>
                  <div className="text-muted-foreground mt-2 flex justify-between text-xs">
                    <span>Order placed</span>
                    <span>Received at FBA</span>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    <div className="bg-muted rounded-2xl p-4">
                      <PackageCheck className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$8.90</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Unit + prep
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <Ship className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$0.94</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Freight per unit
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <ReceiptText className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$0.58</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Duty per unit
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-6 flex items-center gap-3 border-t pt-5">
                    <CheckCircle2 className="text-primary h-5 w-5 shrink-0" />
                    <p className="text-sm font-semibold">
                      Drop this into the calculator instead of guessing at
                      landed cost.
                    </p>
                  </div>
                </div>
              </div>
            </figure>
          </div>
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl">
            <h2 className="text-foreground text-2xl font-bold">
              Related reading
            </h2>
            <ul className="text-muted-foreground mt-4 space-y-3 text-sm leading-7">
              <li>
                <Link
                  href="/blog/how-to-calculate-amazon-fba-landed-cost"
                  className="text-primary font-semibold hover:underline"
                >
                  How to calculate Amazon FBA landed cost
                </Link>
                : inbound cost in one column, the fee stack in the other, and a
                worked unit at $7.66.
              </li>
              <li>
                <Link
                  href="/blog/landed-cost-amazon-fba"
                  className="text-primary font-semibold hover:underline"
                >
                  Amazon FBA landed cost
                </Link>
                : duty, tax, and unsellable returns after the freight quote.
              </li>
              <li>
                <Link
                  href="/tools/landed-cost-calculator"
                  className="text-primary font-semibold hover:underline"
                >
                  Landed cost calculator
                </Link>{' '}
                and{' '}
                <Link
                  href="/blog/how-to-calculate-landed-cost"
                  className="text-primary font-semibold hover:underline"
                >
                  how to calculate landed cost
                </Link>
                : get the inbound number before you paste it above.
              </li>
            </ul>
          </div>
          <RelatedTools currentPath="/tools/amazon-profit-calculator" />
        </section>
      </div>
    </>
  );
}
